import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">About Us</h1>
              <p className="text-purple-400 text-sm">Our Mission & Story</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Hero Section */}
            <div className="text-center py-12">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-6xl mb-6"
              >
                🎓
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Empowering ICSE & ISC Students
              </h1>
              <p className="text-xl text-gray-400">
                Free educational resources for academic excellence
              </p>
            </div>

            {/* Our Story */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">Our Story</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  SolveICSE was founded on <strong className="text-purple-400">May 26, 2026</strong>, with a singular vision: to make high-quality educational resources accessible to every ICSE and ISC student, regardless of their financial circumstances.
                </p>
                <p>
                  We recognized the challenges students face in finding reliable study materials, past papers, and solutions. Our platform bridges this gap by providing comprehensive resources that help students prepare effectively for their board examinations.
                </p>
                <p>
                  What started as a simple idea has grown into a comprehensive educational platform serving thousands of students across India. Our commitment remains unchanged: to empower students with the tools they need to succeed academically.
                </p>
              </div>
            </div>

            {/* Our Mission */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <div className="space-y-4 text-gray-300">
                <p className="text-lg font-medium text-purple-400">
                  "To provide free, high-quality educational resources that enable every ICSE and ISC student to excel in their board examinations."
                </p>
                <ul className="space-y-2 mt-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-400 mt-1">✓</span>
                    <span>Comprehensive past papers and solutions for ICSE Class 9-10 and ISC Class 11-12</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-400 mt-1">✓</span>
                    <span>Textbook solutions and study materials across all major subjects</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-400 mt-1">✓</span>
                    <span>Community-driven learning through interactive features</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-400 mt-1">✓</span>
                    <span>Accessible platform that works on any device</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Our Values */}
            <div className="glass-card rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">Our Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl mb-3">🎯</div>
                  <h3 className="text-white font-medium mb-2">Accessibility</h3>
                  <p className="text-gray-400 text-sm">Education should be free and accessible to everyone</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-3">📚</div>
                  <h3 className="text-white font-medium mb-2">Quality</h3>
                  <p className="text-gray-400 text-sm">Curated, accurate, and comprehensive study materials</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-3">🤝</div>
                  <h3 className="text-white font-medium mb-2">Community</h3>
                  <p className="text-gray-400 text-sm">Building a supportive learning environment</p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="glass-card rounded-2xl p-8 border border-white/10 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Get in Touch</h2>
              <p className="text-gray-400 mb-6">
                Have questions or suggestions? We'd love to hear from you.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
              >
                Contact Us
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
